import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { MemoryPairsScene } from '@/types/gameSpec';
import { shuffle, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

interface MemoryCard {
  cardId: number;     // unique per card
  pairId: number;     // shared between matched cards
  face: string;
  side: 'a' | 'b';
}

export const MemoryPairsSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<MemoryPairsScene>) => {
  const cards = useMemo<MemoryCard[]>(() => {
    const flat: MemoryCard[] = [];
    scene.pairs.forEach((p, pairId) => {
      flat.push({ cardId: pairId * 2,     pairId, face: p.a, side: 'a' });
      flat.push({ cardId: pairId * 2 + 1, pairId, face: p.b, side: 'b' });
    });
    return shuffle(flat);
  }, [scene.id]);

  const [flipped, setFlipped] = useState<number[]>([]); // open card ids
  const [matched, setMatched] = useState<Set<number>>(new Set()); // pair ids
  const [wrongTurns, setWrongTurns] = useState(0);

  const handleFlip = (cardId: number) => {
    if (flipped.includes(cardId) || flipped.length === 2) return;
    const next = [...flipped, cardId];
    setFlipped(next);
    if (next.length === 2) {
      const [a, b] = next.map((id) => cards.find((c) => c.cardId === id)!);
      if (a.pairId === b.pairId) {
        setTimeout(() => {
          setMatched((prev) => new Set(prev).add(a.pairId));
          setFlipped([]);
        }, 500);
      } else {
        setWrongTurns((w) => w + 1);
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  useEffect(() => {
    if (matched.size === scene.pairs.length) {
      // "correct" if solved with no more than 1 wrong turn per pair
      const isCorrect = wrongTurns <= scene.pairs.length;
      setTimeout(
        () => onAnswer(isCorrect, { wrong_turns: wrongTurns }),
        600,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched]);

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {SCENE_LABEL.memory_pairs}
        </div>
        <p className="text-lg font-medium">{scene.prompt}</p>

        <div
          className="grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${Math.min(cards.length, 4)}, minmax(0,1fr))`,
          }}
        >
          {cards.map((c) => {
            const isOpen = flipped.includes(c.cardId) || matched.has(c.pairId);
            const isMatched = matched.has(c.pairId);
            return (
              <button
                key={c.cardId}
                type="button"
                onClick={() => handleFlip(c.cardId)}
                disabled={isMatched}
                className={cn(
                  'aspect-square rounded-md border-2 flex items-center justify-center text-sm font-medium p-2 text-center transition',
                  isOpen
                    ? 'bg-card border-primary'
                    : 'bg-primary/10 border-primary/30 hover:bg-primary/20 cursor-pointer',
                  isMatched && 'border-green-500 bg-green-50 text-green-900',
                )}
              >
                {isOpen ? c.face : '?'}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground">
          Matched {matched.size}/{scene.pairs.length} · Wrong turns {wrongTurns}
        </p>
      </CardContent>
    </Card>
  );
};
